import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Add, ExpandMore, SpaceDashboardOutlined } from "@mui/icons-material"
import { Box, Divider, IconButton, Skeleton, Tabs, Tooltip, Typography, styled, type SxProps, type Theme } from "@mui/material"
import { blue } from "@mui/material/colors"
import { DndContext, DragOverlay, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { OK, ERROR } from "helpers/data-statuses"
import { useAppDispatch } from "hooks/redux"
import { useAccountAlias, useIsMultiDashboardsEnabled } from "resources/account"
import {
  useDashboardInstances,
  type DashboardInstance,
} from "resources/dashboard-instances"
import { useCurrentDashboardId } from "../../context/current-dashboard-id"
import { useArrangementGetter, useSnapshotUnavailableReason } from "../../hooks/use-arrangement-getter"
import { useDuplicateDashboard } from "../../hooks/use-duplicate-dashboard"
import { useRenameDashboard } from "../../hooks/use-rename-dashboard"
import { useDeleteDashboard } from "../../hooks/use-delete-dashboard"
import { useDashboardTabOrder } from "../../hooks/use-dashboard-tab-order"
import DashboardTab, { DashboardTabPreview } from "./dashboard-tab"
import DeleteDashboardDialog from "./delete-dashboard-dialog"
import NewDashboardDialog from "./new-dashboard-dialog"
import RenameDashboardDialog from "./rename-dashboard-dialog"
import LayoutActionsMenu from "./layout-actions-menu"
import SwitcherOverflowMenu from "./switcher-overflow-menu"
import TabActionsMenu from "./tab-actions-menu"

// Stable empty fallback: a fresh [] each render would re-run the sort memo and churn the Tabs.
const EMPTY_INSTANCES: DashboardInstance[] = []

// Selection is driven by DashboardTab's onClick → navigate → URL, not MUI's onChange — but a
// controlled Tabs still requires the prop, or MUI logs a dev-only console error. [APP-3266]
const NO_OP_TAB_CHANGE = (): void => { /* selection navigates via DashboardTab's onClick */ }

// Embeds in the dashboard header (no own background/border). flex:1 fills the old icon/title space;
// minWidth:0 lets the scrollable strip shrink.
const SWITCHER_ROW_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  flex: 1,
  minWidth: 0,
  alignSelf: "stretch",
} satisfies SxProps<Theme>

// Loading skeletons sit a touch further apart than the live tab row.
const LOADING_ROW_SX = { ...SWITCHER_ROW_SX, gap: 1 } satisfies SxProps<Theme>

const INDICATOR_SETTLING_SLOTS = { indicator: { style: { opacity: 0 } } } as const
const INDICATOR_SETTLED_SLOTS = { indicator: { style: { opacity: 1 } } } as const

// Vertical separators between the cluster's zones (switch | create/switch | manage).
const DIVIDER_SX = { mx: 0.5, my: 1 } satisfies SxProps<Theme>

// AGENTS.md's "never hard-code a hex a theme token covers" doesn't apply here: theme.palette.primary
// shifts by mode (#03a9f4 light / #0277bd dark), and this indicator is deliberately mode-INVARIANT —
// no existing token covers a value that must stay put across both. Named (not inlined) so a future
// second use doesn't re-derive it. [APP-3271/3309]
const TAB_INDICATOR_COLOR = blue[500]

// Tabs only ever move along the strip, so vertical drag motion is discarded.
const HORIZONTAL_ONLY = [restrictToHorizontalAxis]

// Module scope so useSensors' internal memo doesn't bust on a fresh object every render.
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 4 } }

// dnd-kit's default screen-reader instructions describe keyboard controls (space bar, arrow keys,
// escape) this drag doesn't support — no keyboard sensor is registered, so they'd be false.
const DRAG_SCREEN_READER_INSTRUCTIONS = {
  draggable: "Press and drag with a pointer to reorder dashboard tabs.",
}
const DRAG_ACCESSIBILITY = { screenReaderInstructions: DRAG_SCREEN_READER_INSTRUCTIONS }

// MUI's indicator repositions on the DOM reorder itself, ahead of dnd-kit's ~250ms slide-into-place
// transition on the moved tabs — the indicator lands on the destination tab's final rect before the
// tab visually arrives there, reading as a jump. Hiding it for the drag + a settle window past
// dnd-kit's default transition duration lets it fade back in once the tabs have actually caught up.
const INDICATOR_SETTLE_MS = 260

// Flat tab row carried by a top indicator bar (no track/pill). A styled component (not a shared sx)
// so the style injects once. [APP-3271]
const DashboardTabs = styled(Tabs)(({ theme }) => ({
  // Fill the header height (override MUI's 48px min) and stretch so the top bar sits flush with the top.
  height: "100%",
  minHeight: 0,
  flex: "0 1 auto",
  maxWidth: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  // v9 renamed the flex container class flexContainer -> list; height:100% here is what
  // vertically centers the tabs and fills the 50px strip (else the list collapses to content height).
  "& .MuiTabs-list": { gap: theme.spacing(2), height: "100%" },
  "& .MuiTabs-indicator": {
    // Top bar (not MUI's default bottom), 3px for legibility.
    top: 0,
    bottom: "unset",
    height: 3,
    borderRadius: "0 0 2px 2px",
    backgroundColor: TAB_INDICATOR_COLOR,
    transition: theme.transitions.create(["left", "width", "opacity"], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
}))

function SingleDashboardTitle() {
  return (
    <Box sx={SWITCHER_ROW_SX}>
      <SpaceDashboardOutlined sx={{ fontSize: 30, color: "common.onBackground" }} />
      <Typography sx={{ fontSize: "18px", lineHeight: 1, color: "common.onBackground", whiteSpace: "nowrap" }}>
        Dashboard
      </Typography>
    </Box>
  )
}

function SwitcherSkeleton() {
  return (
    <Box sx={LOADING_ROW_SX}>
      <Skeleton variant="rounded" width={132} height={26} />
      <Skeleton variant="rounded" width={132} height={26} />
    </Box>
  )
}

export default function DashboardSwitcher() {
  const isEnabled = useIsMultiDashboardsEnabled()
  const currentId = useCurrentDashboardId()

  if (currentId === null) {
    return <SwitcherSkeleton />
  }

  if (!isEnabled) {
    // Flag off: the original single-dashboard chrome — a big dashboard glyph + "Dashboard" label.
    // The switcher tab strip only appears once multi-dashboard is enabled.
    return <SingleDashboardTitle />
  }

  return <InstanceSwitcher currentId={currentId} />
}

function InstanceSwitcher({ currentId }: { currentId: string }) {
  const [instances, status] = useDashboardInstances(true)
  const accountAlias = useAccountAlias()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const getArrangement = useArrangementGetter()
  const { duplicate, isDuplicating } = useDuplicateDashboard(currentId, getArrangement)
  const duplicateUnavailableReason = useSnapshotUnavailableReason(currentId)
  const { rename } = useRenameDashboard()
  const { remove } = useDeleteDashboard()

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null)
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false)
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  // Fires the load-failure toast at most once per mount; a boolean ref (not state) avoids a
  // re-render and keeps the dispatch in the effect, not the render body.
  const erroredToastedRef = useRef(false)

  // Hides the sliding indicator for the drag + its settle window (see INDICATOR_SETTLE_MS).
  const [isIndicatorSettling, setIsIndicatorSettling] = useState(false)
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The tab DragOverlay renders a preview for — set on drag start, cleared on drop/cancel.
  const [activeId, setActiveId] = useState<string | null>(null)

  const { orderedInstances, isOrderLoading, reorder } = useDashboardTabOrder(instances ?? EMPTY_INSTANCES, true)

  const sortableIds = useMemo(
    () => orderedInstances.map((instance) => instance.instanceId),
    [orderedInstances],
  )

  // A few pixels of movement before a drag starts, so a plain click still selects the dashboard.
  // Pointer only, matching how browser tabs reorder; dnd-kit's keyboard sensor would also claim
  // Enter and Space on a focused tab, which do nothing here today.
  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS))

  const clearSettleTimeout = useCallback(() => {
    if (settleTimeoutRef.current === null) return
    clearTimeout(settleTimeoutRef.current)
    settleTimeoutRef.current = null
  }, [])

  // The whole strip can reflow once any tab starts moving, not just the dragged one, so the
  // indicator hides for every drag rather than only ones that end in a reorder.
  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    clearSettleTimeout()
    setIsIndicatorSettling(true)
    setActiveId(String(active.id))
  }, [clearSettleTimeout])

  const scheduleIndicatorReveal = useCallback(() => {
    clearSettleTimeout()
    settleTimeoutRef.current = setTimeout(() => {
      // Null the ref here, not just in clearSettleTimeout — otherwise it keeps pointing at an
      // already-fired id until the next drag, so "current !== null" stops meaning "still pending."
      settleTimeoutRef.current = null
      setIsIndicatorSettling(false)
    }, INDICATOR_SETTLE_MS)
  }, [clearSettleTimeout])

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over) reorder(String(active.id), String(over.id))
      scheduleIndicatorReveal()
      setActiveId(null)
    },
    [reorder, scheduleIndicatorReveal],
  )

  const handleDragCancel = useCallback(() => {
    scheduleIndicatorReveal()
    setActiveId(null)
  }, [scheduleIndicatorReveal])

  useEffect(() => { return clearSettleTimeout }, [clearSettleTimeout])

  // A resource gate must accept ERROR as terminal too, or the bar hangs on the skeleton forever
  // when the fetch errors. On ERROR we render just the "+ New" affordance (and toast once).
  // The saved order gates alongside the instances: tabs drawn before it arrives would show recency
  // order and then jump.
  const isLoading = !(status === OK || status === ERROR) || isOrderLoading

  useEffect(() => {
    if (status !== ERROR || erroredToastedRef.current) return
    erroredToastedRef.current = true
    dispatch(NotificationCreators.add("error", null, "Could not load your dashboards."))
  }, [status, dispatch])

  const handleSelect = useCallback(
    (slug: string) => {
      if (!accountAlias) return
      navigate(`/${accountAlias}/dashboard/${slug}`)
    },
    [accountAlias, navigate],
  )

  const closeMenu = useCallback(() => { setMenuAnchor(null) }, [])

  const openActions = useCallback((anchorEl: HTMLElement) => { setActionsAnchor(anchorEl) }, [])
  const closeActions = useCallback(() => { setActionsAnchor(null) }, [])

  // Both the "+ New" button and the overflow item open the create dialog.
  const openNewDialog = useCallback(() => { setIsNewDialogOpen(true) }, [])
  const closeNewDialog = useCallback(() => { setIsNewDialogOpen(false) }, [])

  const openRenameDialog = useCallback(() => { setIsRenameDialogOpen(true) }, [])
  const closeRenameDialog = useCallback(() => { setIsRenameDialogOpen(false) }, [])
  const openDeleteDialog = useCallback(() => { setIsDeleteDialogOpen(true) }, [])
  const closeDeleteDialog = useCallback(() => { setIsDeleteDialogOpen(false) }, [])

  if (isLoading) {
    return <SwitcherSkeleton />
  }

  // Match the active tab on instanceId or slug (URL may carry either); false on the bare /dashboard
  // landing avoids MUI's out-of-range warning.
  const activeInstance = orderedInstances.find(
    (instance) => instance.instanceId === currentId || instance.slug === currentId,
  )
  const activeValue = activeInstance ? activeInstance.slug : false
  const draggedInstance = orderedInstances.find((instance) => instance.instanceId === activeId)

  return (
    <Box sx={SWITCHER_ROW_SX}>
      {orderedInstances.length > 0 && (
        <DndContext
          sensors={sensors}
          modifiers={HORIZONTAL_ONLY}
          collisionDetection={closestCenter}
          accessibility={DRAG_ACCESSIBILITY}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
            <DashboardTabs
              value={activeValue}
              onChange={NO_OP_TAB_CHANGE}
              variant="scrollable"
              scrollButtons="auto"
              slotProps={isIndicatorSettling ? INDICATOR_SETTLING_SLOTS : INDICATOR_SETTLED_SLOTS}
            >
              {orderedInstances.map((instance) => (
                <DashboardTab
                  key={instance.instanceId}
                  instanceId={instance.instanceId}
                  value={instance.slug}
                  name={instance.name}
                  onSelect={handleSelect}
                  onActions={openActions}
                />
              ))}
            </DashboardTabs>
          </SortableContext>
          <DragOverlay>
            {draggedInstance && <DashboardTabPreview name={draggedInstance.name} />}
          </DragOverlay>
        </DndContext>
      )}
      {/* Divider + overflow switcher — gated together so the empty state isn't a stray divider
          and a "More dashboards" menu listing only "New". */}
      {orderedInstances.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={DIVIDER_SX} />
          <Tooltip title="More dashboards">
            <IconButton
              aria-label="More dashboards"
              size="small"
              onClick={(event) => { setMenuAnchor(event.currentTarget) }}
            >
              <ExpandMore />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="New dashboard">
        <IconButton
          aria-label="New dashboard"
          size="small"
          onClick={openNewDialog}
        >
          <Add />
        </IconButton>
      </Tooltip>
      {/* Set the layout-management menu apart from the create/switch controls — different intent. */}
      <Divider orientation="vertical" flexItem sx={DIVIDER_SX} />
      <LayoutActionsMenu dashboardId={currentId} />
      <SwitcherOverflowMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        instances={orderedInstances}
        currentId={currentId}
        onSelect={handleSelect}
        onNewDashboard={openNewDialog}
      />
      <TabActionsMenu
        anchorEl={actionsAnchor}
        open={Boolean(actionsAnchor) && Boolean(activeInstance)}
        onClose={closeActions}
        onDuplicate={() => {
          // The dashboard (instance) name is the "(copy)" base — duplicate clones the instance, not its layout.
          if (activeInstance) duplicate(activeInstance.layoutId, activeInstance.name)
        }}
        isDuplicating={isDuplicating}
        duplicateUnavailableReason={duplicateUnavailableReason}
        onRename={openRenameDialog}
        onDelete={openDeleteDialog}
      />
      <NewDashboardDialog open={isNewDialogOpen} onClose={closeNewDialog} />
      {activeInstance && isRenameDialogOpen && (
        <RenameDashboardDialog
          open
          currentName={activeInstance.name}
          onClose={closeRenameDialog}
          onRename={(name) => rename(activeInstance.instanceId, name)}
        />
      )}
      {activeInstance && isDeleteDialogOpen && (
        <DeleteDashboardDialog
          open
          dashboardName={activeInstance.name}
          onClose={closeDeleteDialog}
          onDelete={() => remove(activeInstance.instanceId)}
        />
      )}
    </Box>
  )
}
